import { ApiPaymentRequest } from "../types/ApiPaymentRequest";
import RegApi from "../../apps/registration/enum/RegApi";
import { ApiPayForPurchaseOrderResponse } from "../types/ApiPayForPurchaseOrderResponse";
import ConsoleApi from "apps/console/enums/consoleApi";
import { ApiRegistrationOrganization } from "apps/console/modules/registration/registrationOrganization/details/types";
import { useGetApiData, useGetApiDataAsMutate, usePostApiData } from "shared/datalayer";
import { ApiDivision } from "shared/types/ApiDivision";
import PublicApi from "../enums/PublicApi";
import { ApiRegistrationUtilityDataResponse } from "../types/ApiRegistrationUtilityDataResponse";
import UtilityDataRequestType from "../types/UtilityDataRequestType";
import CommonApi from "shared/enums/CommonApi";
import MyExpApi from "apps/myExperience/enum/MyExpApi";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import { ApiPurchaseOrderPaymentReferenceRequest } from "shared/types/ApiPurchaseOrderPaymentReferenceRequest";
import { useCopies } from "shared/hooks/useCopies";
import { successToast } from "shared/utils/toastNotification";
import { ApiOfflinePurchaseOrderUpdateRequest } from "shared/types/ApiOfflinePurchaseOrderUpdateRequest";
import { RegFieldSchemaByViewTypeQueryProps } from "./types";
import ApiFetchFieldsForViewType from "shared/types/ApiFetchFieldsForViewType";
import { ApiOption } from "shared/types/ApiOption";
import { ApiOptionSearchRequest } from "shared/types/ApiOptionSearchRequest";

export function useMakePayment({ hideLoader, errorHandler }: { hideLoader?: boolean; errorHandler?: any }) {
  return usePostApiData<ApiPayForPurchaseOrderResponse, ApiPaymentRequest>(RegApi.MAKE_PAYMENT, {
    clientOptions: { displayErrorMessage: !Boolean(errorHandler) },
    onError: err => errorHandler?.(err),
    hideLoader
  });
}

export function useGetListSponsoringOrganizationsForRegistration({ registrationId }: { registrationId?: string }) {
  return useGetApiData<Array<ApiRegistrationOrganization>>(
    ConsoleApi.GET_SPONSORING_ORGANIZATIONS_LIST,
    { params: { registrationId } },
    { enabled: Boolean(registrationId) }
  );
}

export function useGetDivisionsForRegistration({ registrationId }: { registrationId?: string }) {
  return useGetApiData<Array<ApiDivision>>(
    ConsoleApi.GET_DIVISIONS_FOR_REGISTRATION,
    { params: { registrationId } },
    { enabled: Boolean(registrationId) }
  );
}

export function useValidateEmailDomain(hideLoader?: boolean) {
  return useGetApiDataAsMutate<boolean>(PublicApi.EMAIL_DOMAIN_VALIDATION, { hideLoader });
}

export function useGetEmailDomainValidation({
  registrationFormId,
  workEmail
}: {
  registrationFormId?: string;
  workEmail?: string;
}) {
  return useGetApiData<boolean>(
    PublicApi.EMAIL_DOMAIN_VALIDATION,
    { params: { registrationFormId, workEmail } },
    { enabled: Boolean(registrationFormId) && Boolean(workEmail) }
  );
}

export function useGetUtilityDataByFeatureModuleContext({
  registrationId,
  featureType,
  module,
  contextEntityType,
  contextEntityId
}: UtilityDataRequestType) {
  return useGetApiData<ApiRegistrationUtilityDataResponse>(
    PublicApi.GET_UTILITY_DATA_DETAIL,
    {
      params: { registrationId, featureType, module, contextEntityType, contextEntityId },
      displayErrorMessage: false
    },
    {
      enabled: Boolean(registrationId && featureType && module && contextEntityType && contextEntityId)
    }
  );
}

export function useDownloadEventSummaryForEventParticipant() {
  return useGetApiDataAsMutate<any>(CommonApi.DOWNLOAD_EVENT_SUMMARY);
}

// Used in MyExperience
export function useUpdateOfflineTransaction() {
  return usePostApiData<ApiPurchaseOrder, ApiPurchaseOrderPaymentReferenceRequest>(MyExpApi.UPDATE_OFFLINE_TRANSACTION);
}

export function useCancelOfflineTransaction() {
  const copies = useCopies();
  return usePostApiData<ApiPurchaseOrder, ApiOfflinePurchaseOrderUpdateRequest>(MyExpApi.CANCEL_OFFLINE_TRANSACTION, {
    onSuccess: () => successToast(copies.get("offline.payment.request.cancel.success.msg"))
  });
}

export function useGetRegFieldSchemaByViewType(props: RegFieldSchemaByViewTypeQueryProps) {
  return useGetApiData<ApiFetchFieldsForViewType>(
    CommonApi.FETCH_FIELDS_BY_VIEWTYPE,
    {
      params: {
        registrationId: props.registrationId,
        viewType: props.viewType,
        registrationParticipantId: props.registrationParticipantId,
        ticketFormId: props.ticketFormId
      }
    },
    {
      enabled: props.isEnabled,
      onSuccess: () => {
        props.successHandler?.();
      },
      onError: () => {
        props.errorHandler?.();
      }
    }
  );
}

export function useGetFieldOptions() {
  return usePostApiData<Array<ApiOption>, ApiOptionSearchRequest>(ConsoleApi.GET_OPTION_FOR_FIELD);
}
